/* eslint-disable no-param-reassign */
/* eslint-disable no-use-before-define */
/* eslint-disable no-await-in-loop */

/*
  OMT_AdPlacementService

  NOTES : We do a few things here to address Facebook issues
  1)  N ads of each type are preloaded based on AD_INSTANCE_COUNTS. This can effect fill rates if the user reloads the game, but ensures ads are ready.
  2)  Because the errors from Facebook are not entirely reliable for determining when an ad fails loading and display logic were decoupled.
  3)  Based on information we read from other users using instant games we re-create an instance on loadAsyc() failures with the exeption of the "ADS_NO_FILLL" error.
  4)  Currently there is a timeout of sorts configured by SHOW_AD_RETRY_DELAY, SHOW_AD_RETRY_COUNT. This just basically waits 5 and checks if an ad is ready.
      This was done so we would not have to rely on Facebooks errors.
  5)  Facebook seems to have a undocumented delay before retrying loadAsync(). 10 seconds seems to be the magic number for a minimum delay.
*/
import { OMT_Utils } from '../utils/OMT_Utils';

// adInstance states
const AD_STATES = {
  IDLE: 0,
  LOADING: 1,
  READY: 2,
};

// adInstance types
export const AD_TYPES = {
  REWARDED: 'REWARDED',
  INTERSTITIAL: 'INTERSTITIAL',
};

// set how many ads of each type to preload. 0 will load on demand.
const AD_INSTANCE_COUNTS = {
  REWARDED: 1,
  INTERSTITIAL: 1,
};

// delay before attempting to reload / fill an failed ad
const AD_RELOAD_DELAY = 30000;
const MAX_AD_RELOAD_ATTEMPTS = 1;

// delay / retries for no ad ready timeout
const SHOW_AD_RETRY_DELAY = 1000;
const SHOW_AD_RETRY_COUNT = 5;

/**
 * Class for handling ad loading / showing
 */
export class OMT_AdPlacementService {
  /**
   * constructor
   * @param {boolean} preloadAds
   */
  constructor(preloadAds) {
    this._init(preloadAds);
  }

  /**
   * main intialization creates intial adInstances
   * @param {boolean} preloadAds
   */
  async _init(preloadAds) {
    // ad instances currently in use
    this._adInstances = [];

    // for tracking unfilled adInstance. We must reuse these for retries.
    this._unfilledAdInstance = {
      [AD_TYPES.REWARDED]: [],
      [AD_TYPES.INTERSTITIAL]: [],
    };

    if (preloadAds) { // preload ad isntances of each type
      if (OMT.feature.rewardedAdsAvailable()) {
        for (let i = 0; i < AD_INSTANCE_COUNTS[AD_TYPES.REWARDED]; i++) {
          await this._createAdInstance(AD_TYPES.REWARDED);
        }
      }
      if (OMT.feature.interstitialAdsAvailable()) {
        for (let i = 0; i < AD_INSTANCE_COUNTS[AD_TYPES.INTERSTITIAL]; i++) {
          await this._createAdInstance(AD_TYPES.INTERSTITIAL);
        }
      }
    }
  }

  /**
   * create an adInstance of the appropriate type
   * @param {string} adType should be AD_TYPES.REWARDED || AD_TYPES.INTERSTITIAL
   */
  async _createAdInstance(adType, retries = 0) {
    let ad;
    let fbAdInstance;
    let placementId;

    try {
      if (this._unfilledAdInstance[adType].length > 0) { // reuse previous unfilled ad instance
        ad = this._unfilledAdInstance[adType].pop();
        this._adInstances.push(ad);
      } else if (adType === AD_TYPES.REWARDED) { // create rewarded ad
        placementId = G.BuildEnvironment.adPlacementIDs.REWARDED;
        fbAdInstance = await FBInstant.getRewardedVideoAsync(placementId);
        ad = new OMT_AdInstance(AD_TYPES.REWARDED, fbAdInstance, AD_STATES.IDLE, placementId);
        this._adInstances.push(ad);
      } else if (adType === AD_TYPES.INTERSTITIAL) { // create interstital ad
        placementId = G.BuildEnvironment.adPlacementIDs.INTERSTITIAL;
        fbAdInstance = await FBInstant.getInterstitialAdAsync(placementId);
        ad = new OMT_AdInstance(AD_TYPES.INTERSTITIAL, fbAdInstance, AD_STATES.IDLE, placementId);
        this._adInstances.push(ad);
      }
    } catch (error) { // ad instance could not be created
      this._logAdErrorEvent('adLoadFail', error);
      return;
    }

    // begin preloading this ad now
    this._preloadAd(ad, retries);
  }

  /**
   * start preloading an ad
   * @param {OMT_AdInstance} ad
   */
  async _preloadAd(ad, retries = 0) {
    try { // attempt to preload
      ad.state = AD_STATES.LOADING;
      this._logAdEvent('adLoadRequest');
      await ad.fbAdInstance.loadAsync();
      ad.state = AD_STATES.READY;
      OMT_Utils.stylizedLog(`OMT_AdPlacementService: ad (${ad.adType}) loaded successfully`);
      this._logAdEvent('adLoadSuccess');
    } catch (error) { // failed to preload
      ad.state = AD_STATES.IDLE;
      this._logAdErrorEvent('adLoadFail', error);

      if (retries >= MAX_AD_RELOAD_ATTEMPTS) { // max retries hit
        OMT_Utils.stylizedLog(`OMT_AdPlacementService: ad (${ad.adType}) failed to load (error: ${error.code}), no retries remaining`, '#FFFF00');
        if (error.code === 'ADS_NO_FILL') this._unfilledAdInstance[ad.adType].push(ad);
        this._adInstances.splice(this._adInstances.indexOf(ad), 1);
      } else { // retry loading ad
        OMT_Utils.stylizedLog(`OMT_AdPlacementService: ad (${ad.adType}) failed to load (error: ${error.code}), retry in ${AD_RELOAD_DELAY} ms.`, '#FFFF00');
        if (error.code === 'ADS_NO_FILL') { // call loadAsync again since ad could not be filled
          setTimeout(() => { this._preloadAd(ad, retries + 1); }, AD_RELOAD_DELAY);
        } else { // other errors re-create the ad and remove this ad instance
          this._adInstances.splice(this._adInstances.indexOf(ad), 1);
          setTimeout(() => { this._createAdInstance(ad.adType, retries + 1); }, AD_RELOAD_DELAY);
        }
      }
    }
  }

  /**
   * get a preloaded adInstance of a specific type
   * @param {string} adType
   * @returns {OMT_AdInstance}
   */
  _getPreloadedAd(adType) {
    const preloadedAds = this._adInstances.filter((ad) => ad.adType === adType && ad.state === AD_STATES.READY);
    const ad = preloadedAds.length > 0 ? preloadedAds.shift() : null;
    if (ad) this._adInstances.splice(this._adInstances.indexOf(ad), 1);
    return ad;
  }

  /**
   * get instance count of a specific type of ad
   * @param {string} adType
   */
  _getAdInstanceCount(adType) {
    return this._adInstances.filter((ad) => ad.adType === adType).length;
  }

  /**
   * show an ad. you dont really need to await this.
   * @param {Object} placement placement Object
   * @param {Function} callback
   */
  async showAd(placement, onAdSuccess, onAdFail, retryCount = SHOW_AD_RETRY_COUNT) {
    const { adType } = placement;

    // create on demand instance if we are not preloading
    const adInstanceCount = this._getAdInstanceCount(adType);
    if (adInstanceCount === 0 && retryCount === SHOW_AD_RETRY_COUNT) {
      this._createAdInstance(adType);
    }

    // get a preloaded ad
    const ad = this._getPreloadedAd(adType);
    if (!ad) { // no ad instance available
      if (retryCount > 0) { // retry a few times before failing
        retryCount--;
        await new Promise((resolve) => setTimeout(resolve, SHOW_AD_RETRY_DELAY));
        await this.showAd(placement, onAdSuccess, onAdFail, retryCount);
      } else { // no retries left just fail
        onAdFail();
        this._logAdErrorEvent('adShowFail', null, 'TIME_OUT');
      }
    } else { // show pre-loaded ad now
      try {
        if (AD_INSTANCE_COUNTS[adType] > 0) this._createAdInstance(adType);
        this._logAdEvent('adShowRequest');
        await ad.fbAdInstance.showAsync();
        this._logAdEvent('adShowSuccess');
        onAdSuccess();
      } catch (error) {
        this._logAdErrorEvent('adShowFail', error);
        onAdFail();
      }
    }
  }

  /**
   * log a error in the ad flow
   * @param {string} eventName
   * @param {Error} error
   */
  _logAdErrorEvent(eventName, error = null, altCode = 'NO_ERROR_CODE') {
    const errorCode = error && error.code ? error.code : altCode;
    OMT.platformTracking.logEvent(eventName, null, { error: errorCode });
  }

  /**
   * log a ad success
   * @param {string} eventName
   */
  _logAdEvent(eventName) {
    OMT.platformTracking.logEvent(eventName);
  }
}

/**
 * class for storing data related to FB adInstances
 */
class OMT_AdInstance {
  /**
   * constructor
   * @param {string} adType should be AD_TYPES.REWARDED || AD_TYPES.INTERSTITIAL
   * @param {Object} fbAdInstance FB adInstance
   * @param {number} state as defined in AD_STATES
   * @param {string} placementId FB ad placementID
   */
  constructor(adType, fbAdInstance, state, placementId) {
    this._adType = adType;
    this._fbAdInstance = fbAdInstance;
    this._state = state;
    this._placementId = placementId;
  }

  /**
   * get the type of ad AD_TYPES.REWARDED || AD_TYPES.INTERSTITIAL
   * @returns {string}
   */
  get adType() {
    return this._adType;
  }

  /**
   * get the FB adInstance
   * @returns {Object}
   */
  get fbAdInstance() {
    return this._fbAdInstance;
  }

  /**
   * get the state of the ad defined in AD_STATES
   * @returns {number}
   */
  get state() {
    return this._state;
  }

  /**
   * set the state of the ad
   * @param {number} state
   */
  set state(state) {
    this._state = state;
  }

  /**
   * get the placement ID
   * @returns {string}
   */
  get placementId() {
    return this._placementId;
  }
}
